export const FORMAT_MARKERS = {
  bold: '**',
  italic: '_',
  strikethrough: '~~',
};

const SAFE_LINK_PROTOCOLS = /^(https?|mailto|tel):/i;

const TEXTAREA_MIRROR_STYLES = [
  'border-bottom-width',
  'border-left-width',
  'border-right-width',
  'border-top-width',
  'box-sizing',
  'direction',
  'font-family',
  'font-size',
  'font-stretch',
  'font-style',
  'font-variant',
  'font-weight',
  'height',
  'letter-spacing',
  'line-height',
  'overflow-wrap',
  'padding-bottom',
  'padding-left',
  'padding-right',
  'padding-top',
  'tab-size',
  'text-align',
  'text-indent',
  'text-transform',
  'white-space',
  'width',
  'word-spacing',
  'word-wrap',
];

export const textareaCharacterPosition = (textarea, position) => {
  const style = window.getComputedStyle(textarea);
  const mirror = document.createElement('div');
  const marker = document.createElement('span');

  TEXTAREA_MIRROR_STYLES.forEach((property) => {
    mirror.style.setProperty(property, style.getPropertyValue(property));
  });

  mirror.style.left = '-9999px';
  mirror.style.position = 'fixed';
  mirror.style.top = '0';
  mirror.style.visibility = 'hidden';
  mirror.style.whiteSpace = 'pre-wrap';
  mirror.textContent = textarea.value.slice(0, position);
  marker.textContent = textarea.value.slice(position) || '.';
  mirror.appendChild(marker);
  document.body.appendChild(mirror);

  const textareaRect = textarea.getBoundingClientRect();
  const coordinates = {
    left: textareaRect.left + marker.offsetLeft - textarea.scrollLeft,
    top: textareaRect.top + marker.offsetTop - textarea.scrollTop,
  };

  document.body.removeChild(mirror);
  return coordinates;
};

const isWrappedSelection = (selection, marker) => {
  if (selection.length < marker.length * 2) {
    return false;
  }
  if (!selection.startsWith(marker) || !selection.endsWith(marker)) {
    return false;
  }
  return true;
};

const markerOccurrencesBefore = (value, marker, end) => {
  let count = 0;
  let index = value.indexOf(marker);
  while (index !== -1 && index < end) {
    count += 1;
    index = value.indexOf(marker, index + marker.length);
  }
  return count;
};

const findOpeningMarker = (value, marker, selectionStart) => {
  let openingIndex = null;
  let count = 0;
  let index = value.indexOf(marker);
  while (index !== -1 && index < selectionStart) {
    if (count % 2 === 0) {
      openingIndex = index;
    }
    count += 1;
    index = value.indexOf(marker, index + marker.length);
  }
  return openingIndex;
};

const findClosingMarker = (value, marker, selectionEnd) => {
  let index = value.indexOf(marker, selectionEnd);
  while (index !== -1) {
    if (markerOccurrencesBefore(value, marker, index) % 2 === 1) {
      return index;
    }
    index = value.indexOf(marker, index + marker.length);
  }
  return null;
};

export const selectedMarkdownFormatRange = (value, start, end, marker) => {
  const selection = value.slice(start, end);
  if (isWrappedSelection(selection, marker)) {
    return {
      markerStart: start,
      contentStart: start + marker.length,
      contentEnd: end - marker.length,
      markerEnd: end,
    };
  }

  const openingIndex = findOpeningMarker(value, marker, start);
  if (openingIndex === null) {
    return null;
  }

  const closingIndex = findClosingMarker(value, marker, end);
  if (closingIndex === null) {
    return null;
  }

  const contentStart = openingIndex + marker.length;
  if (start < contentStart || end > closingIndex) {
    return null;
  }

  return {
    markerStart: openingIndex,
    contentStart,
    contentEnd: closingIndex,
    markerEnd: closingIndex + marker.length,
  };
};

export const selectedLinkRange = (value, start, end) => {
  const linkPattern = /\[([^\]\n]+)\]\(([^)\n]+)\)/g;
  let match;
  while ((match = linkPattern.exec(value)) !== null) {
    const fullStart = match.index;
    const fullEnd = linkPattern.lastIndex;
    const label = match[1];
    const url = match[2];
    const labelStart = fullStart + 1;
    const labelEnd = labelStart + label.length;
    const selectionIsInsideLink = start >= fullStart && end <= fullEnd;
    const selectionContainsLabel = start <= labelStart && end >= labelEnd;
    if (selectionIsInsideLink || selectionContainsLabel) {
      return {fullStart, fullEnd, label, labelStart, labelEnd, url};
    }
  }
  return null;
};

export const normalizeMarkdownUrl = (url) => {
  if (url.startsWith('#') || SAFE_LINK_PROTOCOLS.test(url)) {
    return url;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    return null;
  }
  return `https://${url}`;
};
